import React from "react";
import date from "./dateAdapter.js";
import * as http from "./http.ts";
import * as utils from "./utils.jsx";
import * as workparty from "./workparty.jsx";

import { useBackendData, useFormState } from "./hooks.ts";

var FormField = utils.FormField;

function ApplicationHeader({ user, step: currentStep }) {
  var steps = [
    { id: "identify", label: "Identify" },
    { id: "apply", label: "Apply" },
  ];

  return (
    <div>
      <h2>You are signed in as {user.screen_name}</h2>
      <h1>Youth Volunteer Vacation Application</h1>
      <div className="ui progress">
        {steps.map((step, i) => (
          <div
            key={i}
            className={"bar" + (step.id === currentStep ? " on" : "")}
          >
            {i + 1}. {step.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactSelector({
  user,
  actions,
  initialValue,
  updateApplication,
  setStep,
}) {
  const dependents =
    useBackendData({
      url: window.cms_base_url + "vms-user",
      withCredentials: true,
      afterLoad,
    })?.dependents || [];

  const {
    value,
    error,
    errors,
    submitting,
    setRequestState,
    handleChange,
    handleSubmit,
  } = useFormState({
    initialValue,
    handleSubmitted,
  });

  function afterLoad(data) {
    // if user has no dependents, skip to next step
    if (!data.dependents.length) {
      handleSubmit();
    }
  }

  function renderSelector() {
    if (dependents.length) {
      // has dependents
      return (
        <div>
          <label>Choose the person who is applying:</label>
          <div>
            <FormField
              type="radio"
              name="contactId"
              label={user.first_name + " " + user.last_name}
              inline={true}
              checked={value.contactId === user.contact_id}
              value={user.contact_id}
              errors={errors}
              onChange={handleChange}
            />
            {dependents.map((dependent) => (
              <FormField
                type="radio"
                name="contactId"
                label={dependent.first_name + " " + dependent.last_name}
                inline={true}
                checked={value.contactId === dependent.contact_id}
                value={dependent.contact_id}
                key={dependent.contact_id}
                errors={errors}
                onChange={handleChange}
              />
            ))}
          </div>
          <br />
        </div>
      );
    } else {
      // single logged-in user
      return (
        <ol>
          <li>
            <div className="field">
              <label>Name</label>
              <div>{user.first_name + " " + user.last_name}</div>
            </div>
          </li>
        </ol>
      );
    }
  }

  function handleSubmitted() {
    setRequestState((current) => ({ ...current, submitting: true }));
    updateApplication(value);
    verifyDocuments();
  }

  const [documents, setDocuments] = React.useState([]);

  function verifyDocuments() {
    const app = value;
    http
      .getJson(actions.verify, { query: { c: app.contactId } })
      .then((data) => {
        var documents = [];
        data.documents.forEach((wrapper) => {
          wrapper.documentInfos.forEach((doc) => {
            documents.push(doc);
          });
        });
        if (documents.some((doc) => !doc.completed)) {
          setDocuments(data.documents);
        } else {
          setStep("apply", data.contact);
        }
      })
      .catch((error) => {
        setRequestState((current) => ({
          ...current,
          ...error.state,
        }));
      });
  }

  return (
    <div>
      <ApplicationHeader user={user} step="identify" />
      <section className="main">
        <article className="wrapper">
          <section className="utility">
            <form className="utility">
              {documents.length ? (
                <workparty.WorkPartyPreregDocuments
                  user={user}
                  data={documents}
                />
              ) : (
                <div>
                  <fieldset>{renderSelector()}</fieldset>
                  <button
                    onClick={handleSubmit}
                    className="ui button"
                    disabled={submitting}
                  >
                    {submitting ? "Checking documents..." : "Continue"}
                  </button>
                  {error ? <div className="validation-error">{error}</div> : ""}
                </div>
              )}
            </form>
          </section>
        </article>
      </section>
    </div>
  );
}

// Main application form
function ApplicationForm({
  user,
  action,
  initialValue,
  contact,
  workparties,
  feeText,
  setStep,
}) {
  const { value, error, errors, submitting, handleChange, handleSubmit } =
    useFormState({
      action,
      initialValue,
      handleSubmitted,
    });

  function handleSubmitted() {
    setStep("confirm");
  }

  const app = value;
  var FormField = utils.FormField;
  var wps = workparties;
  var wp_names = ["one", "two", "three", "four"];
  var wp_options = {};
  for (var i = 0; i < wp_names.length; i++) {
    var name = wp_names[i];
    var options = [];
    var wp_name = "workparty_" + name;
    var other_values = [];
    options.push({ _id: "", title: "--" });
    for (var j = 0; j < wp_names.length; j++) {
      if (wp_names[j] !== name)
        other_values.push(app["workparty_" + wp_names[j]]);
    }
    for (var k = 0; k < wps.length; k++) {
      var wp = wps[k];
      if (wp._id == app[wp_name] || other_values.indexOf(wp._id) < 0) {
        var start_date = date.tz(wp.start_date_time, "America/Los_Angeles");
        options.push({
          key: wp._id,
          title: wp.name + " - " + date.format(start_date, "EEE, MMM d, yyyy"),
        });
      }
    }
    wp_options[name] = options;
  }
  return (
    <div>
      <ApplicationHeader user={user} step="apply" />
      <section className="main">
        <article className="wrapper">
          <section className="utility">
            <form className="utility">
              <div className="row">
                <div className="col twelve">
                  <h3>Youth Contact Info</h3>
                  <div>
                    <div>{contact.name}</div>
                    <div>{contact.email}</div>
                    {contact.npe01__preferred_phone && contact.phone ? (
                      <div>
                        {contact.npe01__preferred_phone}: {contact.phone}
                      </div>
                    ) : (
                      ""
                    )}
                    {contact.mailing_street ? (
                      <div>{contact.mailing_street}</div>
                    ) : (
                      ""
                    )}
                    {contact.mailing_city &&
                    contact.mailing_state &&
                    contact.mailing_postal_code ? (
                      <div>
                        {contact.mailing_city}, {contact.mailing_state}{" "}
                        {contact.mailing_postal_code}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>
              <br />
              <hr />
              <div className="row">
                <div className="col six">
                  <div className="field">
                    <FormField
                      type="text"
                      name="school_name"
                      label="School Name"
                      value={app.school_name}
                      errors={errors}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col three">
                  <div className="field">
                    <FormField
                      type="text"
                      name="grad_year"
                      label="High School Graduation Year"
                      value={app.grad_year}
                      errors={errors}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col three">
                  <FormField
                    type="select"
                    name="shirt_size"
                    label="T-Shirt Size"
                    value={app.shirt_size}
                    errors={errors}
                    onChange={handleChange}
                  >
                    {utils.SHIRT_SIZES.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </FormField>
                </div>
              </div>
              <br />
              <hr />
              <h2>Preferred Workparties</h2>
              <div className="row">
                <div className="col six">
                  <FormField
                    type="select"
                    name="workparty_one"
                    label="1st Choice"
                    required
                    value={app.workparty_one}
                    errors={errors}
                    onChange={handleChange}
                  >
                    {wp_options.one.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.title}
                      </option>
                    ))}
                  </FormField>
                </div>
                <div className="col six">
                  <FormField
                    type="select"
                    name="workparty_two"
                    label="2nd Choice"
                    required
                    value={app.workparty_two}
                    errors={errors}
                    onChange={handleChange}
                  >
                    {wp_options.two.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.title}
                      </option>
                    ))}
                  </FormField>
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col six">
                  <FormField
                    type="select"
                    name="workparty_three"
                    label="3rd Choice"
                    value={app.workparty_three}
                    errors={errors}
                    onChange={handleChange}
                  >
                    {wp_options.three.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.title}
                      </option>
                    ))}
                  </FormField>
                </div>
                <div className="col six">
                  <FormField
                    type="select"
                    name="workparty_four"
                    label="4th Choice"
                    value={app.workparty_four}
                    errors={errors}
                    onChange={handleChange}
                  >
                    {wp_options.four.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.title}
                      </option>
                    ))}
                  </FormField>
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col six">
                  <h2>
                    Are you interested in carpooling with other volunteers to
                    your trip locations?
                  </h2>
                  <FormField
                    type="select"
                    name="car_pool"
                    value={app.car_pool}
                    errors={errors}
                    onChange={handleChange}
                  >
                    <option>No Thanks</option>
                    <option>Needs a ride</option>
                    <option>Willing to drive</option>
                    <option>Willing to drive or ride</option>
                  </FormField>
                </div>
              </div>
              <br />
              <div>
                <h2>
                  Please list a convenient meeting point near you to meet for
                  carpooling.
                </h2>
                <FormField
                  type="textarea"
                  name="car_pool_location_preference"
                  rows="5"
                  style={{ height: "50px" }}
                  value={app.car_pool_location_preference}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <br />
              <div>
                <h2>Application Comments</h2>
                <em>
                  Please let us know here if you have specific questions or
                  concerns about the application process.
                </em>
                <FormField
                  type="textarea"
                  name="application_comments"
                  rows="5"
                  style={{ height: "100px" }}
                  value={app.application_comments}
                  errors={errors}
                  onChange={handleChange}
                />
              </div>
              <br />
              <hr />
              <h2>Fee</h2>
              <div className="row">
                <div className="col twelve">
                  <p>{feeText}</p>
                </div>
              </div>
              <hr />
              <h2>Scholarship Information</h2>
              <div className="row">
                <div className="col eight">
                  <p>
                    If cost is a barrier to your participation, consider
                    applying for a Youth Volunteer Vacation Scholarship. Look
                    for the link to apply in your application confirmation
                    email.
                  </p>
                </div>
                <div className="col four">
                  <FormField
                    type="checkbox"
                    name="request_scholarship"
                    label="I&rsquo;m interested in applying"
                    value={app.request_scholarship}
                    errors={errors}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <hr />
              <h2>
                How did you hear about this trip, and why are you interested in
                participating?
              </h2>
              <FormField
                type="textarea"
                name="hear"
                rows="5"
                style={{ height: "100px" }}
                value={app.hear}
                errors={errors}
                onChange={handleChange}
              />
              <br />
              <h2>
                Describe your interest and/or experience with hiking, camping,
                and/or trail maintenance, including any time with WTA.
              </h2>
              <FormField
                type="textarea"
                name="experience"
                rows="5"
                style={{ height: "100px" }}
                value={app.experience}
                errors={errors}
                onChange={handleChange}
              />
              <br />
              <br />
              <h2>
                What would you like your crew leaders to know about you? How can
                we help make this trip a success for you?
              </h2>
              <FormField
                type="textarea"
                name="about_you"
                rows="5"
                style={{ height: "100px" }}
                value={app.about_you}
                errors={errors}
                onChange={handleChange}
              />
              <br />
              <br />
              <h2>
                What are you hoping to get out of this experience? What will you
                contribute to the success of your crew?
              </h2>
              <FormField
                type="textarea"
                name="contribution"
                rows="5"
                style={{ height: "100px" }}
                value={app.contribution}
                errors={errors}
                onChange={handleChange}
              />
              <br />
              <hr />
              <div className="ui">
                <button
                  className="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Apply"}
                </button>
              </div>
              <br />
              {error ? (
                <div className="validation-error">
                  <div className="container">{error}</div>
                </div>
              ) : (
                ""
              )}
            </form>
          </section>
        </article>
      </section>
    </div>
  );
}

// application confirmation / thank you page
function ApplicationConfirmation() {
  return (
    <div>
      <section className="main">
        <article>
          <section className="layout primary">
            <header role="heading">
              <h1>Thank You</h1>
              <h2>Application Summary</h2>
            </header>
            <hr />
            <p>
              Thank you for applying, we will contact you with further
              information once your application has been reviewed.
            </p>
          </section>
          <section className="layout secondary">
            <div className="module dialogue">
              <div className="bubble">
                <p>
                  Congratulations!
                  <br />
                  Your application has been submitted!
                </p>
              </div>
              <img src={window.base_url + "static/images/avatar.01.png"} />
            </div>
          </section>
        </article>
      </section>
      <a href={window.base_url} className="ui button green">
        Back to Find a Work Party
      </a>
      <br />
      <br />
    </div>
  );
}

// main application component which controls progression through the above steps
function YouthApplicationFlow({
  application,
  user,
  selected,
  step,
  workparties,
  feeText,
  actions,
}) {
  function getInitialState() {
    var app = application || {
      contactId: user ? user.contact_id : null,
      workparty_one: selected,
      car_pool: "No Thanks",
    };
    return {
      application: app,
      step: step ? step : "identify",
    };
  }

  const [state, setState] = React.useState(getInitialState);

  const previousStepRef = React.useRef(state.step);
  React.useEffect(() => {
    if (previousStepRef.current !== state.step) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      previousStepRef.current = state.step;
    }
  }, [state.step]);

  function updateApplication(value) {
    setState((currentState) => ({ ...currentState, application: value }));
  }

  function setStep(step, contact) {
    setState((currentState) => ({
      ...currentState,
      contact: contact,
      step: step,
    }));
  }

  switch (state.step) {
    case "confirm":
      return <ApplicationConfirmation />;
    case "login":
      var url = window.location.href;
      window.location.href =
        window.cms_base_url + "/login?came_from=" + encodeURIComponent(url);
      return null;
    case "apply":
      return (
        <ApplicationForm
          user={user}
          initialValue={state.application}
          contact={state.contact}
          action={actions.apply}
          setStep={setStep}
          workparties={workparties}
          feeText={feeText}
        />
      );
    default:
      return (
        <ContactSelector
          user={user}
          initialValue={state.application}
          actions={actions}
          updateApplication={updateApplication}
          setStep={setStep}
        />
      );
  }
}

export default YouthApplicationFlow;
